import { useQuery } from "@tanstack/react-query";
import { Languages, Loader2 } from "lucide-react";
import { Api, type AudioTrack } from "../lib/api.js";

/**
 * Audio-language switcher.
 *
 * Multi-audio releases are the rule, not the exception: anime rips ship
 * Japanese + English, European groups ship the original plus two dubs, and
 * discs add a commentary track. Whichever one the muxer flagged as default is
 * what any player picks with no preference expressed — which is how you end up
 * watching in Japanese with no way out.
 *
 * The server picks English by default and returns the whole list. Switching is
 * a URL edit on the HLS session (each track is its own transcode session), so
 * it costs a player re-mount and nothing else; the caller restores the
 * position. Only rendered when there is an actual choice to make.
 */
export function AudioTrackPicker({
  tracks,
  active,
  onSelect,
}: {
  tracks: AudioTrack[];
  /** Currently mapped track index, or null when the server hasn't said. */
  active: number | null;
  onSelect: (index: number) => void;
}): JSX.Element | null {
  if (tracks.length < 2) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1.5 text-xs text-parchment/70 font-display tracking-smallcaps">
        <Languages size={13} /> Audio
      </span>
      {tracks.map((t) => {
        const isActive = t.index === active;
        return (
          <button
            key={t.index}
            onClick={() => { if (!isActive) onSelect(t.index); }}
            title={[
              t.title,
              t.is_default ? "flagged default by the release" : null,
              t.is_commentary ? "commentary track" : null,
            ].filter(Boolean).join(" · ") || undefined}
            className={
              isActive
                ? "px-2.5 py-1 rounded-full text-xs font-display tracking-smallcaps bg-gold/25 border border-goldHi text-ivory"
                : "px-2.5 py-1 rounded-full text-xs font-display tracking-smallcaps bg-elev border border-hairline text-parchment hover:text-ivory hover:border-gold/60 transition"
            }
          >
            {t.label}
            {t.is_commentary && <span className="opacity-60"> · commentary</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Read the source URL back out of an HLS session URL.
 *
 * The session URL carries the release's URL as a query parameter, and the
 * status endpoint is keyed on that same pair — so callers can recover it
 * rather than threading it separately through the player.
 */
export function hlsSourceUrl(streamUrl: string): string | null {
  if (!streamUrl.includes("/api/hls/session.m3u8")) return null;
  return new URLSearchParams(streamUrl.split("?")[1] ?? "").get("url");
}

/**
 * Live transcode progress for the stream being watched.
 *
 * Video is stream-copied, so the writer normally runs several times faster
 * than playback and the whole film becomes seekable within minutes. When it
 * doesn't — a slow debrid route, a contended box — this is the difference
 * between "be patient" and "pick another release".
 */
export function TranscodeProgressChip({ streamUrl, audio }: {
  /** The HLS session URL currently loaded in the player. */
  streamUrl: string;
  audio: number | null;
}): JSX.Element | null {
  const sourceUrl = hlsSourceUrl(streamUrl);
  // Re-encode (v=h264) sessions are keyed separately server-side; the status
  // lookup has to carry the flag or it reports the copy-mode sibling.
  const sessionParams = streamUrl.includes("/api/hls/session.m3u8")
    ? new URLSearchParams(streamUrl.split("?")[1] ?? "")
    : null;
  const videoMode = sessionParams?.get("v") ?? null;
  const startS = sessionParams?.get("start") ?? null;

  const { data } = useQuery({
    queryKey: ["hls-status", sourceUrl, audio, videoMode, startS],
    queryFn: () => Api.hls.status(sourceUrl!, audio, videoMode, startS),
    enabled: Boolean(sourceUrl),
    // Only while it's still being written; a complete session never changes.
    refetchInterval: (q) => (q.state.data?.state === "complete" ? false : 4000),
  });

  if (!sourceUrl || !data || data.state === "idle") return null;
  if (data.state === "failed") {
    return (
      <span className="text-xs text-vermilionHi" title={data.error ?? undefined}>
        transcode failed
      </span>
    );
  }
  if (data.state === "complete") {
    return <span className="text-xs text-jade" title="Fully transcoded and cached — seeking is instant">cached ✓</span>;
  }
  const pct = data.progress !== null && data.progress !== undefined
    ? Math.round(data.progress * 100)
    : null;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-parchment/80"
      title="Seeking works up to this point; the transcoder runs ahead of playback"
    >
      <Loader2 size={12} className="animate-spin" />
      {pct !== null ? `${pct}% ready` : `${Math.floor((data.cached_s ?? 0) / 60)} min ready`}
    </span>
  );
}
